import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Shield, Award, Globe } from "lucide-react";
import { Globe3D } from "@/components/3d/Globe3D";
import { FloatingShapes } from "@/components/3d/FloatingShapes";

const trustBadges = [
  { icon: Shield, label: "Verified Suppliers" },
  { icon: Award, label: "Quality Assured" },
  { icon: Globe, label: "100+ Countries" },
];

export function HeroSection() {
  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden perspective-1000">
      {/* 3D Globe Background */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[800px] h-[800px] max-w-full max-h-full">
          <Globe3D />
        </div>
      </div>

      {/* Floating 3D Shapes */}
      <FloatingShapes />

      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/70 to-background" />
      
      {/* Decorative glow elements */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: "2s" }} />

      {/* Grid overlay for depth */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(to right, hsl(43 70% 50%) 1px, transparent 1px),
            linear-gradient(to bottom, hsl(43 70% 50%) 1px, transparent 1px)
          `,
          backgroundSize: "80px 80px",
        }}
      />

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif font-bold leading-tight mb-6 animate-fade-up">
            <span className="text-gradient-gold">VIATRA</span>
            <span className="text-foreground"> — Your Gateway to Premium Indian Exports</span>
          </h1>

          {/* Subtext */}
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10 leading-relaxed animate-fade-up" style={{ animationDelay: "0.1s" }}>
            From product sourcing to international delivery, we connect global buyers with 
            India's finest manufacturers. B2B trade solutions for distributors, retailers, 
            and enterprises worldwide.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <Link to="/contact">
              <Button variant="gold" size="xl" className="group relative overflow-hidden">
                <span className="relative z-10">Partner With Us</span>
                <div className="absolute inset-0 bg-gold-light opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="goldOutline" size="xl" className="backdrop-blur-sm">
                Request Trade Inquiry
              </Button>
            </Link>
          </div>

          {/* Trust Badges with 3D hover effect */}
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            {trustBadges.map((badge) => (
              <div
                key={badge.label}
                className="flex items-center gap-3 text-muted-foreground group cursor-default"
              >
                <div className="p-2 rounded-lg bg-secondary border border-border group-hover:border-primary/50 transition-all duration-300 group-hover:shadow-[0_0_20px_hsl(43_70%_47%/0.2)]"
                  style={{ transform: "translateZ(0)" }}
                >
                  <badge.icon className="w-5 h-5 text-primary" />
                </div>
                <span className="font-medium group-hover:text-foreground transition-colors">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
